<template>
  <BModal
    :model-value="modal"
    scrollable
    centered
    title="Schedule"
    @update:modelValue="updateModal"
  >
    <template #header>
      <div
        class="d-flex align-items-center justify-content-between modal-title w-100"
      >
        <h5>Schedule</h5>
      </div>
      <BButton variant="link" class="close" @click="updateModal(false)">
        <i class="ion ion-android-close"></i>
      </BButton>
    </template>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="text-center p-4">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">Loading schedule data...</p>
    </div>

    <BForm v-else>
      <!-- Housekeeping Selection -->
      <div class="form-group">
        <BFormSelect
          v-model="scheduleForm.employee_uuid"
          :options="staffOptions"
          class="form-control mb-3"
          :disabled="!staffOptionsLoaded"
        >
          <template #first>
            <BFormSelectOption :value="null">
              {{
                staffOptionsLoaded
                  ? "Select Housekeeping"
                  : "Housekeeping position not found"
              }}
            </BFormSelectOption>
          </template>
        </BFormSelect>
      </div>

      <div class="form-group">
        <BFormInput type="date" v-model="scheduleForm.schedule" class="mb-3">
        </BFormInput>
      </div>

      <!-- Room Category and Room Numbers -->
      <div
        v-for="(categoryGroup, catIndex) in roomCategoryGroups"
        :key="'cat-' + catIndex"
        class="form-group mb-4"
      >
        <!-- Room Category Select -->
        <BRow class="mb-2">
          <BCol md="9">
            <BFormSelect
              v-model="categoryGroup.category_uuid"
              :options="roomCategories"
              class="form-select"
              :disabled="!roomCategoriesLoaded"
              @change="() => handleRoomCategoryChange(catIndex)"
            >
              <template #first>
                <BFormSelectOption :value="null" disabled>
                  {{
                    roomCategoriesLoaded
                      ? "Select Room Type"
                      : "Loading room types..."
                  }}
                </BFormSelectOption>
              </template>
            </BFormSelect>
          </BCol>
          <BCol md="2" class="d-flex">
            <BButton
              variant="light"
              @click="addRoomCategory"
              title="Add Category"
              :disabled="!roomCategoriesLoaded"
            >
              <i class="fas fa-plus"></i>
            </BButton>
            <BButton
              variant="light"
              class="ml-2"
              @click="removeRoomCategory(catIndex)"
              title="Remove Category"
              :disabled="!roomCategoriesLoaded"
            >
              <i class="fas fa-minus"></i>
            </BButton>
          </BCol>
        </BRow>

        <!-- Room Number Select(s) -->
        <BRow
          v-for="(roomNumber, roomIdx) in categoryGroup.roomNumbers"
          :key="'room-' + catIndex + '-' + roomIdx"
          class="mb-2"
        >
          <BCol md="9">
            <BFormSelect
              v-model="roomNumber.selectedUuid"
              :options="roomNumber.availableRooms"
              class="form-select"
              :disabled="!roomCategoriesLoaded"
              @change="() => onRoomNumberChange(catIndex)"
            >
              <template #first>
                <BFormSelectOption :value="null" disabled>
                  Select Room Number
                </BFormSelectOption>
              </template>
            </BFormSelect>
          </BCol>
          <BCol md="2" class="d-flex">
            <BButton
              variant="light"
              @click="addRoomNumber(catIndex)"
              :disabled="!roomCategoriesLoaded"
            >
              <i class="fas fa-plus"></i>
            </BButton>
            <BButton
              variant="light"
              class="ml-2"
              @click="deleteRoomNumber(catIndex, roomIdx)"
              :disabled="!roomCategoriesLoaded"
            >
              <i class="fas fa-minus"></i>
            </BButton>
          </BCol>
        </BRow>
      </div>
    </BForm>

    <template #footer>
      <div class="d-flex justify-content-between">
        <div>
          <BButton
            variant="primary"
            @click="handleAction"
            class="mr-2"
            :disabled="isLoading || !roomCategoriesLoaded"
          >
            {{ scheduleData && scheduleData.uuid ? "Update" : "Save" }}
          </BButton>
          <BButton variant="danger" @click="updateModal(false)">Cancel</BButton>
        </div>
      </div>
    </template>
  </BModal>
</template>

<script>
import { ref, onMounted, watch, inject } from "vue";
import {
  BRow,
  BCol,
  BModal,
  BButton,
  BFormSelect,
  BFormSelectOption,
  BForm,
  BFormInput,
} from "bootstrap-vue-next";

export default {
  name: "ScheduleForm",
  props: {
    modal: Boolean,
    scheduleData: {
      type: Object,
      default: null,
    },
  },
  components: {
    BRow,
    BCol,
    BModal,
    BButton,
    BFormSelect,
    BFormSelectOption,
    BForm,
    BFormInput,
  },
  setup(props, { emit }) {
    const repositories = inject("repositories");
    const staffRepository = repositories.employee;
    const roomCategoryRepository = repositories.roomCategory;
    const roomRepository = repositories.rooms;
    const scheduleRepository = repositories.schedule;

    const selectHousekeeping = ref(null);
    const staffOptions = ref([]);
    const roomCategories = ref([]);
    const roomCategoriesLoaded = ref(false);
    const staffOptionsLoaded = ref(false);
    const isLoading = ref(false);

    // Cache untuk mengurangi API calls
    const roomsCache = ref([]);
    const bookedRoomsCache = ref([]);
    const currentEditRoomUuids = ref([]);

    // Cache global untuk data yang jarang berubah
    const globalRoomsCache = ref([]);
    const globalCategoriesCache = ref([]);
    const globalStaffCache = ref([]);

    const roomCategoryGroups = ref([
      {
        category_uuid: null,
        roomNumbers: [
          {
            selectedUuid: null,
            availableRooms: [],
          },
        ],
      },
    ]);

    const scheduleForm = ref({
      employee_uuid: null,
      schedule: new Date().toISOString().slice(0, 10),
    });

    const resetFormData = () => {
      scheduleForm.value = {
        employee_uuid: null,
        schedule: new Date().toISOString().slice(0, 10),
      };
      roomCategoryGroups.value = [
        {
          category_uuid: null,
          roomNumbers: [
            {
              selectedUuid: null,
              availableRooms: [],
            },
          ],
        },
      ];
      currentEditRoomUuids.value = [];
    };

    // Preload static data saat komponen dimount
    const preloadStaticData = async () => {
      try {
        // Load data yang jarang berubah secara paralel
        const [allRoomsRes, allCategoriesRes, staffRes] = await Promise.all([
          roomRepository.getAll(),
          roomCategoryRepository.getAll(),
          staffRepository.getHousekeeping(),
        ]);

        // Cache data global
        globalRoomsCache.value = allRoomsRes.data.data || [];
        globalCategoriesCache.value = allCategoriesRes.data.data || [];
        globalStaffCache.value = staffRes.data.data || [];

        // Set staff options langsung dari cache
        staffOptions.value = globalStaffCache.value.map((item) => ({
          value: item.uuid,
          text: item.fullname,
        }));
        staffOptionsLoaded.value = true;
      } catch (error) {
        console.error("preloadStaticData error:", error);
      }
    };

    // Load dynamic data berdasarkan tanggal
    const loadDynamicData = async (selectedDate) => {
      if (!selectedDate) return;

      try {
        // Hanya load booked rooms karena data lain sudah di-cache
        const bookedRoomsRes = await scheduleRepository.getBookedRooms(
          selectedDate
        );
        bookedRoomsCache.value = bookedRoomsRes.data.data || [];

        // Update rooms cache dengan data global
        roomsCache.value = [...globalRoomsCache.value];
      } catch (error) {
        console.error("loadDynamicData error:", error);
      }
    };

    // Optimized getRoomCategories
    const getRoomCategories = async () => {
      try {
        const selectedDate = scheduleForm.value.schedule;
        if (!selectedDate) {
          roomCategories.value = [];
          return;
        }

        // Get fully booked categories
        const bookedCategoriesRes =
          await scheduleRepository.getFullyBookedCategories(selectedDate);
        const bookedCategoryUuids = bookedCategoriesRes.data.data;

        // Filter categories dari cache global
        const filteredCategories = globalCategoriesCache.value.filter(
          (item) => {
            // Jika tidak fully booked, tampilkan
            if (!bookedCategoryUuids.includes(item.uuid)) {
              return true;
            }

            // Jika fully booked, cek apakah ada room dari category ini yang sedang di-edit
            if (
              props.scheduleData?.uuid &&
              currentEditRoomUuids.value.length > 0
            ) {
              const categoryRooms = globalRoomsCache.value.filter(
                (room) => room.room_category?.uuid === item.uuid
              );
              const hasEditableRooms = categoryRooms.some((room) =>
                currentEditRoomUuids.value.includes(room.uuid)
              );
              return hasEditableRooms;
            }

            return false;
          }
        );

        roomCategories.value = filteredCategories.map((item) => ({
          value: item.uuid,
          text: item.name,
          name: item.name,
        }));

        roomCategoriesLoaded.value = true;
      } catch (error) {
        console.error("getRoomCategories error:", error);
        roomCategoriesLoaded.value = true; // Set true meskipun error agar UI tidak stuck
      }
    };

    // Batch processing untuk mode edit
    const processEditMode = async () => {
      if (!props.scheduleData?.uuid) return;

      try {
        // Set current edit room UUIDs
        currentEditRoomUuids.value = [];
        props.scheduleData.room_category?.forEach((category) => {
          category.rooms.forEach((room) => {
            currentEditRoomUuids.value.push(room.uuid);
          });
        });

        // Set form data
        scheduleForm.value.employee_uuid =
          props.scheduleData.employee?.uuid || null;
        scheduleForm.value.schedule = props.scheduleData.schedule || null;

        // Process room category groups
        const newRoomCategoryGroups = [];

        for (const category of props.scheduleData.room_category || []) {
          const selectedRoomUuids = category.rooms.map((room) => room.uuid);
          const availableRooms = getRoomsByCategory(
            category.uuid,
            selectedRoomUuids
          );

          const validRoomNumbers = category.rooms
            .filter((room) =>
              availableRooms.some((available) => available.uuid === room.uuid)
            )
            .map((roomObj) => ({
              selectedUuid: roomObj.uuid,
              availableRooms: availableRooms.map((r) => ({
                value: r.uuid,
                text: r.code || r.name || r.number,
              })),
            }));

          if (validRoomNumbers.length > 0) {
            newRoomCategoryGroups.push({
              category_uuid: category.uuid,
              roomNumbers: validRoomNumbers,
            });
          }
        }

        // Set room category groups
        roomCategoryGroups.value = newRoomCategoryGroups;
      } catch (error) {
        console.error("processEditMode error:", error);
      }
    };

    // Gunakan cache untuk getRoomsByCategory
    const getRoomsByCategory = (categoryValue, selectedRoomUuids = []) => {
      try {
        const filteredRooms = roomsCache.value.filter((room) => {
          const isSameCategory = room.room_category?.uuid === categoryValue;
          const isStatusAllowed = ["dirty", "inhouse"].includes(
            room.status?.toLowerCase()
          );
          const isBookedByOthers = bookedRoomsCache.value.includes(room.uuid);
          const isSelectedByThisSchedule = selectedRoomUuids.includes(
            room.uuid
          );
          const isCurrentlyBeingEdited = currentEditRoomUuids.value.includes(
            room.uuid
          );

          return (
            isSameCategory &&
            isStatusAllowed &&
            (!isBookedByOthers ||
              isSelectedByThisSchedule ||
              isCurrentlyBeingEdited)
          );
        });

        return filteredRooms;
      } catch (error) {
        console.error("getRoomsByCategory error:", error);
        return [];
      }
    };

    const handleRoomCategoryChange = async (categoryIndex) => {
      const categoryGroup = roomCategoryGroups.value[categoryIndex];
      categoryGroup.roomNumbers = [
        {
          selectedUuid: null,
          availableRooms: [],
        },
      ];

      const rooms = getRoomsByCategory(categoryGroup.category_uuid);
      updateAvailableRoomsForCategory(categoryIndex, rooms);
    };

    const updateAvailableRoomsForCategory = (categoryIndex, rooms) => {
      const selectedUuids = roomCategoryGroups.value[categoryIndex].roomNumbers
        .map((r) => r.selectedUuid)
        .filter(Boolean);

      roomCategoryGroups.value[categoryIndex].roomNumbers.forEach(
        (roomNumber) => {
          const currentUuid = roomNumber.selectedUuid;
          const available = rooms.filter(
            (room) =>
              !selectedUuids.includes(room.uuid) || room.uuid === currentUuid
          );
          roomNumber.availableRooms = available.map((room) => ({
            value: room.uuid,
            text: room.code || room.name || room.number,
            room_data: room,
          }));
        }
      );
    };

    const onRoomNumberChange = (categoryIndex) => {
      const category_uuid =
        roomCategoryGroups.value[categoryIndex].category_uuid;
      const selectedUuids = getSelectedRoomUuidsInCategory(categoryIndex);
      const rooms = getRoomsByCategory(category_uuid, selectedUuids);
      updateAvailableRoomsForCategory(categoryIndex, rooms);
    };

    const getSelectedRoomUuidsInCategory = (categoryIndex) => {
      return roomCategoryGroups.value[categoryIndex].roomNumbers
        .map((r) => r.selectedUuid)
        .filter(Boolean);
    };

    const addRoomCategory = () => {
      roomCategoryGroups.value.push({
        category_uuid: null,
        roomNumbers: [
          {
            selectedUuid: null,
            availableRooms: [],
          },
        ],
      });
    };

    const removeRoomCategory = (index) => {
      if (roomCategoryGroups.value.length > 1) {
        roomCategoryGroups.value.splice(index, 1);
      }
    };

    const addRoomNumber = (categoryIndex) => {
      roomCategoryGroups.value[categoryIndex].roomNumbers.push({
        selectedUuid: null,
        availableRooms: [],
      });
      const category_uuid =
        roomCategoryGroups.value[categoryIndex].category_uuid;
      const selectedUuids = getSelectedRoomUuidsInCategory(categoryIndex);
      const rooms = getRoomsByCategory(category_uuid, selectedUuids);
      updateAvailableRoomsForCategory(categoryIndex, rooms);
    };

    const deleteRoomNumber = (categoryIndex, index) => {
      const group = roomCategoryGroups.value[categoryIndex];
      if (group.roomNumbers.length <= 1) return;

      const keptRoomUuids = group.roomNumbers
        .filter((_, idx) => idx !== index)
        .map((r) => r.selectedUuid)
        .filter(Boolean);

      group.roomNumbers.splice(index, 1);

      group.roomNumbers.forEach((room) => {
        const currentUuid = room.selectedUuid;
        const filtered = room.availableRooms.filter(
          (roomOption) =>
            !keptRoomUuids.includes(roomOption.value) ||
            roomOption.value === currentUuid
        );
        room.availableRooms = filtered;
      });
    };

    const handleAction = () => {
      if (props.scheduleData?.uuid) {
        update();
      } else {
        create();
      }
    };

    const updateModal = (value) => {
      emit("update:modal", value);
      if (!value) {
        resetFormData();
        // Reset loading states
        isLoading.value = false;
        roomCategoriesLoaded.value = false;
        // Clear dynamic cache saat modal ditutup
        roomsCache.value = [];
        bookedRoomsCache.value = [];
      }
    };

    const create = async () => {
      const room_categories = [];

      roomCategoryGroups.value.forEach((group) => {
        if (group.category_uuid) {
          const room_uuids = group.roomNumbers
            .map((room) => room.selectedUuid)
            .filter(Boolean);

          if (room_uuids.length > 0) {
            room_categories.push({
              category_uuid: group.category_uuid,
              room_uuids: room_uuids,
            });
          }
        }
      });

      const payload = {
        employee_uuid: scheduleForm.value.employee_uuid,
        schedule: scheduleForm.value.schedule,
        room_categories: room_categories,
      };

      await scheduleRepository
        .create(payload)
        .then(() => {
          updateModal(false);
          resetFormData();
          emit("addOrUpdate");
        })
        .catch((err) => {
          console.log("data tidak berhasil disimpan", err);
        });
    };

    const update = async () => {
      const room_categories = [];

      roomCategoryGroups.value.forEach((group) => {
        if (group.category_uuid) {
          const room_uuids = group.roomNumbers
            .map((room) => room.selectedUuid)
            .filter(Boolean);

          if (room_uuids.length > 0) {
            room_categories.push({
              category_uuid: group.category_uuid,
              room_uuids: room_uuids,
            });
          }
        }
      });

      const payload = {
        employee_uuid: scheduleForm.value.employee_uuid,
        schedule: scheduleForm.value.schedule,
        room_categories: room_categories,
      };

      await scheduleRepository
        .update(payload, props.scheduleData.uuid)
        .then(() => {
          updateModal(false);
          resetFormData();
          emit("addOrUpdate");
        })
        .catch((err) => {
          console.log(err);
        });
    };

    watch(
      () => scheduleForm.value.schedule,
      async (newDate) => {
        if (newDate && props.modal) {
          roomCategoriesLoaded.value = false;
          await loadDynamicData(newDate);
          await getRoomCategories();
        }
      }
    );

    watch(
      () => props.modal,
      async (val) => {
        if (val) {
          isLoading.value = true;
          roomCategoriesLoaded.value = false;

          try {
            // Jika data static belum di-load, load dulu
            if (globalRoomsCache.value.length === 0) {
              await preloadStaticData();
            } else {
              // Gunakan cache yang sudah ada
              roomsCache.value = [...globalRoomsCache.value];
              if (!staffOptionsLoaded.value) {
                staffOptions.value = globalStaffCache.value.map((item) => ({
                  value: item.uuid,
                  text: item.fullname,
                }));
                staffOptionsLoaded.value = true;
              }
            }

            // Load dynamic data
            await loadDynamicData(scheduleForm.value.schedule);

            // Process mode edit jika ada
            if (props.scheduleData?.uuid) {
              await processEditMode();
            } else {
              resetFormData();
            }

            // Get room categories
            await getRoomCategories();
          } catch (error) {
            console.error("Modal open error:", error);
          } finally {
            isLoading.value = false;
          }
        }
      }
    );

    // **OPTIMASI 8: Preload data saat komponen di-mount**
    onMounted(async () => {
      // Preload static data di background
      await preloadStaticData();
    });

    return {
      selectHousekeeping,
      staffOptions,
      roomCategories,
      handleRoomCategoryChange,
      onRoomNumberChange,
      addRoomNumber,
      deleteRoomNumber,
      updateModal,
      handleAction,
      scheduleForm,
      create,
      update,
      addRoomCategory,
      removeRoomCategory,
      roomCategoryGroups,
      roomCategoriesLoaded,
      staffOptionsLoaded,
      isLoading,
    };
  },
};
</script>
